import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Globe } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  useGetSurveyAssignmentsQuery,
  useLazyGetSurveyAssignmentsQuery,
} from "@/redux/service";
import { isPaginatedResponse } from "@/redux/typeGuards";
import {
  Capabilities,
  Roles,
  SIP_TUTORIAL_URL,
  SipPreviews,
  SurveyStatus,
  SurveyType,
  OrganizationRoles,
} from "@/shared/utils/constants";
import OverdueSurveysReminder from "./components/OverdueSurveysReminder";
import CompleteProfileBanner from "./components/CompleteProfileBanner";
import CompleteProfileTypesBanner from "./components/CompleteProfileTypesBanner";
import { setIsFirstLogin } from "@/redux/authentication/reducer";
import BaseButton from "@/components_v2/Button/BaseButton";
import PrmePrinciples from "@/assets/icons/prme-commons-principles.png";
import BaseDropdownButton from "@/components_v2/Button/BaseDropdownButton";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { BUTTON_TYPES } from "@/components_v2/Button/constants";
import { skipToken } from "@reduxjs/toolkit/query";
import { RootState } from "@/redux/store";
import { hasCapability } from "@/shared/utils/roles";

import "./styles.css";

const Home = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isFirstLogin = useSelector(
    (state: RootState) => state.auth.isFirstLogin,
  );
  const [getOverdueSurveyInvites, overdueSurveyInvitesResponse] =
    useLazyGetSurveyAssignmentsQuery();
  const currentUser = useSelector(selectCurrentUser);
  const canAccessSurvey = hasCapability(
    currentUser,
    Capabilities.ACCESS_SURVEY,
  );
  const { data: surveyAssignmentsData, isSuccess: surveyAssignmentsSuccess } =
    useGetSurveyAssignmentsQuery(
      currentUser && currentUser.organization && canAccessSurvey
        ? { organization: currentUser.organization.id, pagination: "off" }
        : skipToken,
    );
  const [isReminderVisible, setIsReminderVisible] = useState(true);

  const canViewSurveyGuidance =
    currentUser?.organizationRole &&
    [
      OrganizationRoles.ADMIN.value,
      OrganizationRoles.SUPERVISOR.value,
    ].includes(currentUser.organizationRole);

  const activeSipSurvey = useMemo(() => {
    if (!surveyAssignmentsSuccess) {
      return null;
    }

    const assignments = isPaginatedResponse(surveyAssignmentsData)
      ? surveyAssignmentsData.results
      : surveyAssignmentsData;

    const activeSipAssignments = assignments
      .filter(
        ({ survey }) =>
          survey.type === SurveyType.SIP.value &&
          survey.status === SurveyStatus.ACTIVE.value,
      )
      .sort(
        (a, b) =>
          new Date(b.survey.startDate).getTime() -
          new Date(a.survey.startDate).getTime(),
      );

    if (activeSipAssignments.length === 0) {
      return null;
    }
    return activeSipAssignments[0]?.survey ?? null;
  }, [surveyAssignmentsSuccess, surveyAssignmentsData]);

  const [selectedLanguage, setSelectedLanguage] = useState(
    SipPreviews.EN.value,
  );

  const sipPreviewMenuProps = {
    items: SipPreviews.asList().map(
      (opt: { value: string; label: string }) => ({
        key: opt.value,
        label: opt.label,
        onClick: () => setSelectedLanguage(opt.value),
      }),
    ),
  };

  const handleDownloadPreview = () => {
    const preview = SipPreviews[
      selectedLanguage as keyof typeof SipPreviews
    ] as { link: string } | undefined;
    const link = preview?.link ?? SipPreviews.EN.link;
    window.open(link, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (
      currentUser &&
      currentUser.role !== Roles.ADMIN.value &&
      canAccessSurvey
    ) {
      getOverdueSurveyInvites({ pagination: "off", overdue: true });
    }
  }, [currentUser, canAccessSurvey]);

  useEffect(() => {
    if (isFirstLogin) {
      if (canAccessSurvey) {
        window.Storylane.Play({
          type: "popup",
          demo_type: "image",
          width: 2560,
          height: 1299.2,
          scale: "0.95",
          demo_url: SIP_TUTORIAL_URL,
          padding_bottom: "calc(50.75% + 25px)",
        });
      }
      dispatch(setIsFirstLogin(false));
    }
  }, [isFirstLogin, canAccessSurvey]);

  return (
    currentUser && (
      <div className="onboarding-container flex full-height">
        <div className="onboarding-main-content main-content-container">
          {overdueSurveyInvitesResponse.isSuccess &&
          Array.isArray(overdueSurveyInvitesResponse.data) &&
          overdueSurveyInvitesResponse.data.length > 0 &&
          isReminderVisible ? (
            <OverdueSurveysReminder
              setIsVisible={setIsReminderVisible}
              surveys={overdueSurveyInvitesResponse.data.map((i) => i.survey)}
            />
          ) : null}

          <div className="onboarding-content-wrapper flex gap-400">
            <div className="onboarding-text-section flex flex-col gap-300">
              <h1 className="header-l-medium">
                {t("home.labels.welcomeToCommons")}
              </h1>

              <div className="onboarding-description">
                <p className="body-s-regular">
                  {t("home.labels.onboardingPageContent")}
                </p>
              </div>

              <div className="onboarding-actions flex gap-300 flex-wrap">
                {activeSipSurvey && canViewSurveyGuidance && (
                  <div className="onboarding-actions-card flex flex-col gap-100">
                    <p className="body-m-medium">
                      {t("home.labels.viewSurveyGuidanceLabel", {
                        surveyType: activeSipSurvey.type,
                      })}
                    </p>

                    <p className="body-s-regular">
                      {t("home.labels.viewSurveyGuidanceDescription", {
                        surveyType: activeSipSurvey.type,
                      })}
                    </p>

                    <BaseButton
                      type={BUTTON_TYPES.BUTTON}
                      onClick={() =>
                        navigate(`/reports/${activeSipSurvey.id}/guidance`)
                      }
                    >
                      {t("home.labels.viewSurveyGuidance", {
                        surveyType: activeSipSurvey.type,
                      })}
                    </BaseButton>
                  </div>
                )}

                {canAccessSurvey && (
                  <div className="onboarding-actions-card flex flex-col gap-100">
                    <p className="body-m-medium">
                      {t("home.labels.referenceMaterials")}
                    </p>

                    <p className="body-s-regular">
                      {t("home.labels.referenceMaterialsDescription", {
                        surveyTitle:
                          activeSipSurvey?.title || SurveyType.SIP.value,
                      })}
                    </p>

                    <BaseDropdownButton
                      className="guidance-page-download-preview-button"
                      menu={sipPreviewMenuProps}
                      onClick={handleDownloadPreview}
                      icon={
                        <div className="flex items-center gap-200">
                          <Globe size={16} />
                          <ChevronDown size={20} />
                        </div>
                      }
                    >
                      {t("home.labels.downloadSurvey", {
                        surveyTitle:
                          activeSipSurvey?.title || SurveyType.SIP.value,
                      })}{" "}
                      ({SipPreviews.getLabel(selectedLanguage)})
                    </BaseDropdownButton>
                  </div>
                )}

                <CompleteProfileBanner />
                <CompleteProfileTypesBanner />
              </div>
            </div>

            <div className="onboarding-image-section flex items-center justify-center">
              <img
                src={PrmePrinciples}
                alt="prme commons principles"
                className="onboarding-image"
              />
            </div>
          </div>
        </div>
      </div>
    )
  );
};

export default Home;
